import { defineComponent as _defineComponent } from 'vue'
import { onMounted, onUnmounted, ref, toRefs, withDefaults } from 'vue';
  import Modal from '@/components/content/Modal.vue';
  import FileDropZone from '@/components/common/fileUploadModal/FileDropZone.vue';
  import BaseText from '@ui/atoms/baseText/BaseText.vue';
  import EmptyState from '@ui/emptyState/EmptyState.vue';
  import {
    ALLOWED_MAX_FILE_NAME_LENGTH,
    ALLOWED_MAX_FILE_SIZE_IN_MB,
    ALLOWED_MAX_IMAGE_SIZE_IN_MB,
    AREA_UPLOAD_FOLDER_NAME,
  } from '@/constant/const';
  import {
    FileUploadOption,
    FileUploadStatus,
    Folder,
    TemporaryPDFPlaylistItem,
    TemporaryPlaylistItem,
  } from '@/components/common/fileUploadModal/types';
  import FileUploadRow from '@/components/common/fileUploadModal/FileUploadRow.vue';
  import { apiGetFileUploadStatus, apiUploadFile, apiUploadPDF } from '@/api/content';
  import { pdfToFiles } from '@/helpers/pdf';
  import { useToasted } from '@/helpers';
  import { sleep } from '@/helpers/async';

  
  const __default__ = { name: 'FileUploadModal' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    folders: { type: Array, required: true },
    selectedFolder: { type: null, required: true },
    allowMultiple: { type: Boolean, required: false, default: true },
    awaitProcessing: { type: Boolean, required: false, default: true },
    fromArea: { type: Boolean, required: false }
  },
  emits: ["close", "success"],
  setup(__props: any, { emit }: { emit: ({
    (
      event: 'close',
      data: {
        shouldRehydrate: boolean;
        playlistItems: Array<TemporaryPlaylistItem | TemporaryPDFPlaylistItem>;
      },
    ): void;
    (
      event: 'success',
      data: {
        shouldRehydrate: boolean;
        playlistItems: Array<TemporaryPlaylistItem | TemporaryPDFPlaylistItem>;
      },
    ): void;
  }), expose: any, slots: any, attrs: any }) {

const props = __props as { folders: Folder[], selectedFolder: Folder | null, allowMultiple: boolean, awaitProcessing: boolean, fromArea?: boolean };

  
  const { folders, selectedFolder } = toRefs(props);

  

  const toasted = useToasted();

  const modalRef = ref(null);

  const selectedFolderId = ref<string>(
    selectedFolder.value === null ? 'null' : selectedFolder.value.id.toString(),
  );

  const isMounted = ref(true);

  const fileToUploadOptions = ref(new Array<FileUploadOption>());
  const temporaryPlaylistItems = ref(new Array<TemporaryPlaylistItem | TemporaryPDFPlaylistItem>());
  const isUploading = ref(false);
  const isSuccess = ref(false);
  const isError = ref(false);
  const errorMessage = ref<string | null>(null);
  const shouldRehydrate = ref(false);

  onMounted(() => {
    if (modalRef.value) {
      modalRef.value.open();
      isMounted.value = true;
    }
    if (props.fromArea) {
      const areaFolder = folders.value.find((folder) => folder.name === AREA_UPLOAD_FOLDER_NAME);
      selectedFolderId.value = areaFolder ? areaFolder.id.toString() : 'null';
    }
  });

  onUnmounted(() => {
    isMounted.value = false;
  });

  function handleFilesSelect(data: { selectedFiles: File[] }) {
    for (const file of data.selectedFiles) {
      const exists = fileToUploadOptions.value.some(
        (fileUploadOption) => fileUploadOption.file.name === file.name,
      );

      if (file.name.length > ALLOWED_MAX_FILE_NAME_LENGTH) {
        toasted.global.general_error({
          message: `File name cannot be longer than ${ALLOWED_MAX_FILE_NAME_LENGTH} characters`,
        });

        continue;
      }

      const fileSizeInMB = file.size / 1024 ** 2;
      if (
        (file.type === 'image/png' || file.type === 'image/jpeg') &&
        fileSizeInMB > ALLOWED_MAX_IMAGE_SIZE_IN_MB
      ) {
        toasted.global.general_error({
          message: `Images cannot be larger than ${ALLOWED_MAX_IMAGE_SIZE_IN_MB} MB`,
        });

        continue;
      } else if (file.type === 'video/mp4' && fileSizeInMB > ALLOWED_MAX_FILE_SIZE_IN_MB) {
        toasted.global.general_error({
          message: `Videos cannot be larger than ${ALLOWED_MAX_FILE_SIZE_IN_MB} MB`,
        });

        continue;
      } else if (file.type === 'audio/mpeg' && fileSizeInMB > ALLOWED_MAX_FILE_SIZE_IN_MB) {
        toasted.global.general_error({
          message: `Audio files cannot be larger than ${ALLOWED_MAX_FILE_SIZE_IN_MB} MB`,
        });

        continue;
      }

      if (!exists) {
        if (props.allowMultiple) {
          fileToUploadOptions.value.push({
            file: file,
            status: null,
            percent: 0,
          } as FileUploadOption);
        } else {
          fileToUploadOptions.value = [
            { file: file, status: null, percent: 0 } as FileUploadOption,
          ];
        }
      }
    }
  }

  function handleFileRemove(name: string) {
    const deleteIndex = fileToUploadOptions.value.findIndex(
      (fileUploadOption) => fileUploadOption.file.name === name,
    );

    if (deleteIndex > -1) {
      // the backend don't support aborting a file upload once the process has started
      const fileUploadOption = fileToUploadOptions.value[deleteIndex];
      if (
        fileUploadOption.status === FileUploadStatus.Uploading ||
        fileUploadOption.status === FileUploadStatus.PendingProcess ||
        fileUploadOption.status === FileUploadStatus.Processing
      ) {
        toasted.global.general_warning({
          message:
            'This file is currently being uploaded or processed. Please wait for the upload to finish before removing it.',
        });
        return;
      }

      fileToUploadOptions.value.splice(deleteIndex, 1);
    }
  }

  async function handleMediaFileUpload() {
    isUploading.value = true;
    isSuccess.value = false;
    isError.value = false;
    shouldRehydrate.value = false;
    errorMessage.value = null;

    for (const fileUploadOption of fileToUploadOptions.value) {
      if (fileUploadOption.status == null || fileUploadOption.status === FileUploadStatus.Failed) {
        var updatedFileUploadOption: FileUploadOption = {
          ...fileUploadOption,
          status: FileUploadStatus.Uploading,
        };
        setFileToUploadOption(updatedFileUploadOption);

        try {
          const folderId =
            selectedFolderId.value === 'null' ? null : parseInt(selectedFolderId.value);

          if (updatedFileUploadOption.file.type === 'application/pdf') {
            updatedFileUploadOption = await processAndUploadPdf(updatedFileUploadOption, folderId);
          } else {
            updatedFileUploadOption = await uploadFile(updatedFileUploadOption, folderId);
          }

          if (updatedFileUploadOption.status === FileUploadStatus.Completed) {
            shouldRehydrate.value = true;
            setFileToUploadOption({ ...updatedFileUploadOption });
          } else if (updatedFileUploadOption.status === FileUploadStatus.Failed) {
            setFileToUploadOption({ ...updatedFileUploadOption });

            isUploading.value = false;
            isError.value = true;

            break;
          }
        } catch (error) {
          console.log(error);
          updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Failed };

          setFileToUploadOption(updatedFileUploadOption);

          isUploading.value = false;
          isError.value = true;
          errorMessage.value = (error as any)?.response?.data?.message;

          break;
        }
      }
    }

    isUploading.value = false;

    if (isError.value === false) {
      toasted.global.general_success({ message: 'File upload(s) successful.' });

      isSuccess.value = true;
      emit('success', {
        shouldRehydrate: shouldRehydrate.value,
        playlistItems: temporaryPlaylistItems.value,
      });
    } else {
      toasted.global.general_error({ message: errorMessage.value ?? 'Failed to upload file.' });
    }
  }

  async function processAndUploadPdf(fileUploadOption: FileUploadOption, folderId: number | null) {
    var updatedFileUploadOption = fileUploadOption;

    updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Processing };
    setFileToUploadOption(updatedFileUploadOption);

    const thumbs = await pdfToFiles(fileUploadOption.file);
    const response = await apiUploadPDF(
      fileUploadOption.file,
      thumbs,
      folderId,
      (progressEvent) => {
        updatedFileUploadOption = handleFileUploadProgress(progressEvent, fileUploadOption);
      },
    );
    temporaryPlaylistItems.value.push({ ...response.pdf_content, item_type: 'app/pdf' });

    updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Completed };
    setFileToUploadOption(updatedFileUploadOption);

    return updatedFileUploadOption;
  }

  async function uploadFile(fileUploadOption: FileUploadOption, folderId: number | null) {
    var updatedFileUploadOption = fileUploadOption;

    const fileUpload = await apiUploadFile(
      fileUploadOption.file.name,
      fileUploadOption.file,
      folderId,
      (progressEvent) => {
        updatedFileUploadOption = handleFileUploadProgress(progressEvent, fileUploadOption);
      },
    );
    if (fileUpload.isCompressed) {
      toasted.global.general_notification({
        message: 'We are compressing your video to optimize it for playback.',
      });
    }
    temporaryPlaylistItems.value.push(fileUpload.temporaryPlaylistItem);
    if (props.awaitProcessing) {
      return await pollFileUploadStatus(updatedFileUploadOption, fileUpload.fileUploadId);
    }
    return updatedFileUploadOption;
  }

  async function pollFileUploadStatus(
    fileUploadOption: FileUploadOption,
    fileUploadId: string,
    pollInterval: number = 2000,
  ) {
    var updatedFileUploadOption = fileUploadOption;

    do {
      // kill the ongoing task if the component has unmounted to prevent it from continuing to run in the background
      if (isMounted.value === false) {
        if (
          updatedFileUploadOption.status != null &&
          updatedFileUploadOption.status != FileUploadStatus.Completed
        ) {
          updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Failed };
          setFileToUploadOption(updatedFileUploadOption);
        }
        break;
      }

      const { status } = await apiGetFileUploadStatus(fileUploadId);

      if (status === 'Pending') {
        updatedFileUploadOption = {
          ...updatedFileUploadOption,
          status: FileUploadStatus.PendingProcess,
        };
        setFileToUploadOption(updatedFileUploadOption);
      } else if (status === 'Processing') {
        updatedFileUploadOption = {
          ...updatedFileUploadOption,
          status: FileUploadStatus.Processing,
        };
        setFileToUploadOption(updatedFileUploadOption);
      } else if (status === 'Completed') {
        updatedFileUploadOption = {
          ...updatedFileUploadOption,
          status: FileUploadStatus.Completed,
        };
        setFileToUploadOption(updatedFileUploadOption);
        break;
      } else if (status === 'Failed') {
        updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Failed };
        setFileToUploadOption(updatedFileUploadOption);
        break;
      } else {
        updatedFileUploadOption = { ...updatedFileUploadOption, status: FileUploadStatus.Failed };
        setFileToUploadOption(updatedFileUploadOption);

        console.error('Unsupported file upload status');
        toasted.global.general_error({ message: 'An error occurred during file upload.' });
        break;
      }

      await sleep(pollInterval);
    } while (true);

    return updatedFileUploadOption;
  }

  function handleFileUploadProgress(progressEvent: any, fileUploadOption: FileUploadOption) {
    const { loaded, total } = progressEvent;
    const percent = Math.floor((loaded * 100) / total);

    if (
      (fileUploadOption.percent < 100 && percent === 100) ||
      (fileUploadOption.percent < 100 && percent < 100)
    ) {
      const newFileUploadOption = { ...fileUploadOption, percent: percent };

      setFileToUploadOption(newFileUploadOption);
      return newFileUploadOption;
    }

    return fileUploadOption;
  }

  function handleClose() {
    emit('close', {
      shouldRehydrate: shouldRehydrate.value,
      playlistItems: temporaryPlaylistItems.value,
    });
  }

  function setFileToUploadOption(fileUploadOption: FileUploadOption) {
    const insertIndex = fileToUploadOptions.value.findIndex(
      (option) => option.file.name === fileUploadOption.file.name,
    );

    if (insertIndex > -1) {
      fileToUploadOptions.value.splice(insertIndex, 1, fileUploadOption);
    }
  }

return { __sfc: true,props, folders, selectedFolder, emit, toasted, modalRef, selectedFolderId, isMounted, fileToUploadOptions, temporaryPlaylistItems, isUploading, isSuccess, isError, errorMessage, shouldRehydrate, handleFilesSelect, handleFileRemove, handleMediaFileUpload, processAndUploadPdf, uploadFile, pollFileUploadStatus, handleFileUploadProgress, handleClose, setFileToUploadOption, Modal, FileDropZone, BaseText, EmptyState, ALLOWED_MAX_FILE_SIZE_IN_MB, ALLOWED_MAX_IMAGE_SIZE_IN_MB, FileUploadRow }
}

})